import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Printer, Mail, CalendarClock } from "lucide-react";
import { ujmsHearingsCalendar, ujmsIntakeConfig, ujmsBatchReschedule } from "../../services/ujmsApi";
import { openPdfInViewer } from "../PdfViewer";
import { Panel, Badge, thCls, tdCls, inputCls, selectCls, Field, btnPrimary, btnGhost, cls, can } from "../ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { useOutletContext } from "react-router-dom";
import { CriminalCallBoard } from "../enterprise/calendar/CriminalCallBoard";
import { CourtReports } from "../enterprise/calendar/CourtReports";
import { WeeklyRotationPanel } from '../enterprise/calendar/WeeklyRotationPanel';
import { useAuth } from "../../context/AuthContext";

const plusDays = (n) => new Date(Date.now() + n * 864e5).toISOString().slice(0, 10);

export default function CourtCalendar() {
  const { access } = useOutletContext();
  const [sp, setSp] = useSearchParams();
  const { user } = useAuth();
  const view = sp.get("view") || "calendar";
  const selectedDay = /^\d{4}-\d{2}-\d{2}$/.test(sp.get("date") || "") ? sp.get("date") : null;
  const [from, setFrom] = useState(selectedDay || plusDays(0));
  const [to, setTo] = useState(selectedDay || plusDays(14));
  useEffect(() => { if (selectedDay) { setFrom(selectedDay); setTo(selectedDay); } }, [selectedDay]);
  const [judgeId, setJudgeId] = useState("");
  const [roomId, setRoomId] = useState("");
  const [cfg, setCfg] = useState(null);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState([]);
  const [resetOpen, setResetOpen] = useState(false);
  const [calendarStage, setCalendarStage] = useState("");
  const [hearingStatus, setHearingStatus] = useState("");

  useEffect(() => { ujmsIntakeConfig().then((r) => setCfg(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (view !== "calendar" && !judgeId && cfg?.judges?.length) setJudgeId((cfg.judges.find((j) => j.user_id === user?.id || j.name === user?.name) || cfg.judges[0]).id); }, [view, judgeId, cfg, user]);

  const load = useCallback(() => {
    const params = { date_from: from, date_to: to };
    if (judgeId) params.judge_id = judgeId;
    if (roomId) params.courtroom_id = roomId;
    ujmsHearingsCalendar(params).then((r) => { setRows(r.data); setSelected([]); }).catch(() => {});
  }, [from, to, judgeId, roomId]);

  useEffect(() => { load(); }, [load]);

  const visibleRows = rows.filter((h) => (!calendarStage || h.calendar_stage === calendarStage) && (!hearingStatus || h.status === hearingStatus));
  const byDay = visibleRows.reduce((acc, h) => {
    const d = (h.scheduled_start || "").slice(0, 10);
    (acc[d] = acc[d] || []).push(h);
    return acc;
  }, {});

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const toggleDay = (day) => {
    const ids = byDay[day].filter((h) => h.status === "SCHEDULED").map((h) => h.id);
    const all = ids.every((id) => selected.includes(id));
    setSelected((s) => all ? s.filter((x) => !ids.includes(x)) : [...new Set([...s, ...ids])]);
  };
  const pdfParams = (day) => `date=${day}${roomId ? `&courtroom_id=${roomId}` : ""}${judgeId ? `&judge_id=${judgeId}` : ""}`;
  const nav = <nav className="flex flex-wrap gap-2 border-b pb-2" aria-label="Court calendar views" data-testid="court-calendar-views">{[["calendar", "Hearings calendar"], ["criminal-call", "Criminal call"], ["court-reports", "Court reports"], ['court-weeks','Court weeks']].map(([key, label]) => <button key={key} className={key === view ? btnPrimary : btnGhost} aria-current={key === view ? "page" : undefined} onClick={() => { const next = new URLSearchParams(sp); next.set("view", key); setSp(next); }} data-testid={`calendar-view-${key}`}>{label}</button>)}</nav>;
  if(view==='court-weeks')return <div className="space-y-4 min-w-0" data-testid="court-weeks-view">{nav}<WeeklyRotationPanel/></div>;
  if (view === "criminal-call" || view === "court-reports") return <div className="space-y-4 min-w-0" data-testid="court-calendar-operations"><h1 className="text-2xl font-semibold" data-testid="court-operations-title">Court Calendar</h1>{nav}<div className="flex flex-wrap gap-3"><Field label={view === "criminal-call" ? "Call date" : "Reporting period date"}><input type="date" className={inputCls} value={from} onChange={(e) => setFrom(e.target.value)} data-testid="court-operations-date" /></Field><Field label="Trial judge"><select className={selectCls} value={judgeId} onChange={(e) => setJudgeId(e.target.value)} data-testid="court-operations-judge"><option value="">Select…</option>{cfg?.judges?.map((j) => <option key={j.id} value={j.id}>{j.name}</option>)}</select></Field></div>{view === "criminal-call" ? <CriminalCallBoard judgeId={judgeId} date={from} cfg={cfg} /> : <CourtReports judgeId={judgeId} date={from} access={access} />}</div>;

  return (
    <div className="space-y-4" data-testid="court-calendar">
      {nav}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Court Calendar</h1>
          <p className="text-sm text-slate-500">Scheduled matters across courtrooms and judicial calendars. Select rows to batch-reset a session.</p>
        </div>
        {selected.length > 0 && can(access, "hearing.schedule") && (
          <button className={btnPrimary} onClick={() => setResetOpen(true)} data-testid="batch-reset-btn">
            <CalendarClock className="w-4 h-4" /> Batch Reset ({selected.length})
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <Field label="Calendar status"><select className={selectCls} value={calendarStage} onChange={(e) => setCalendarStage(e.target.value)} data-testid="calendar-stage-filter"><option value="">All statuses</option>{["PRETRIAL", "READY", "PRIORITY", "UNRECORDED"].map((v) => <option key={v}>{v}</option>)}</select></Field>
        <Field label="Hearing status"><select className={selectCls} value={hearingStatus} onChange={(e) => setHearingStatus(e.target.value)} data-testid="calendar-hearing-status-filter"><option value="">All hearings</option>{["SCHEDULED", "READY", "CALLED", "IN_PROGRESS", "COMPLETED", "CONTINUED", "CANCELLED", "NO_APPEARANCE"].map((v) => <option key={v}>{v}</option>)}</select></Field>
        <Field label="From"><input type="date" className={inputCls} value={from} onChange={(e) => setFrom(e.target.value)} data-testid="calendar-from" /></Field>
        <Field label="To"><input type="date" className={inputCls} value={to} onChange={(e) => setTo(e.target.value)} data-testid="calendar-to" /></Field>
        <Field label="Judge">
          <select className={selectCls} value={judgeId} onChange={(e) => setJudgeId(e.target.value)} data-testid="calendar-judge">
            <option value="">All judges</option>
            {(cfg?.judges || []).map((j) => <option key={j.id} value={j.id}>{j.name}</option>)}
          </select>
        </Field>
        <Field label="Courtroom">
          <select className={selectCls} value={roomId} onChange={(e) => setRoomId(e.target.value)} data-testid="calendar-room">
            <option value="">All courtrooms</option>
            {(cfg?.courtrooms || []).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </Field>
      </div>

      {Object.keys(byDay).sort().map((day) => (
        <Panel key={day} title={new Date(day + "T12:00:00").toDateString()} testId={`calendar-day-${day}`}
          action={
            <div className="flex gap-1.5">
              <button className={cls(btnGhost, "px-2 py-1 text-xs")} data-testid={`flex-calendar-${day}`}
                onClick={() => openPdfInViewer(`/ujms/sessions/flex-calendar/pdf?${pdfParams(day)}`, `Flex Calendar — ${day}`)}>
                <Printer className="w-3.5 h-3.5" /> Flex Calendar
              </button>
              <button className={cls(btnGhost, "px-2 py-1 text-xs")} data-testid={`list-envelopes-${day}`}
                onClick={() => openPdfInViewer(`/ujms/sessions/envelopes/pdf?${pdfParams(day)}`, `Session Envelopes — ${day}`)}>
                <Mail className="w-3.5 h-3.5" /> Envelopes
              </button>
            </div>
          }>
          <table className="w-full border-collapse">
            <thead><tr>
              {can(access, "hearing.schedule") && (
                <th className={thCls} style={{ width: 30 }}>
                  <input type="checkbox" aria-label="Select all in this session"
                    checked={byDay[day].filter((h) => h.status === "SCHEDULED").every((h) => selected.includes(h.id)) && byDay[day].some((h) => h.status === "SCHEDULED")}
                    onChange={() => toggleDay(day)} data-testid={`select-day-${day}`} />
                </th>
              )}
              <th className={thCls}>Time</th><th className={thCls}>Case Number</th><th className={thCls}>Caption</th>
              <th className={thCls}>Hearing</th><th className={thCls}>Judge</th><th className={thCls}>Courtroom</th><th className={thCls}>Hearing status</th><th className={thCls}>Calendar status</th>
            </tr></thead>
            <tbody>
              {byDay[day].map((h) => (
                <tr key={h.id} className={cls("hover:bg-slate-50", selected.includes(h.id) && "bg-ujms-soft")}>
                  {can(access, "hearing.schedule") && (
                    <td className={tdCls}>
                      {h.status === "SCHEDULED" && (
                        <input type="checkbox" checked={selected.includes(h.id)} onChange={() => toggle(h.id)}
                          aria-label={`Select ${h.case_number}`} data-testid={`select-hearing-${h.id}`} />
                      )}
                    </td>
                  )}
                  <td className={`${tdCls} font-mono text-xs`}>{(h.scheduled_start || "").slice(11, 16)}</td>
                  <td className={`${tdCls} font-mono text-xs`}>
                    <Link to={`/cases/${h.case_id}`} data-testid={`calendar-case-${h.id}`} className="text-ujms-dark hover:underline">{h.case_number}</Link>
                  </td>
                  <td className={tdCls}><span className="line-clamp-1 max-w-[260px]">{h.caption}</span></td>
                  <td className={tdCls}>{h.hearing_type_label || h.hearing_type}</td>
                  <td className={`${tdCls} text-xs`}>{h.judicial_officer || "—"}</td>
                  <td className={`${tdCls} text-xs`}>{h.courtroom || "—"}</td>
                  <td className={tdCls}><Badge value={h.status} /></td>
                  <td className={tdCls}><Badge value={h.calendar_stage || "UNRECORDED"} testId={`calendar-stage-${h.id}`} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      ))}
      {!visibleRows.length && <Panel><p className="p-6 text-sm text-slate-400" data-testid="calendar-no-matches">No hearings match this date window and calendar status.</p></Panel>}

      <BatchResetDialog open={resetOpen} onClose={() => setResetOpen(false)} ids={selected} cfg={cfg}
        onDone={() => { setResetOpen(false); load(); }} />
    </div>
  );
}

function BatchResetDialog({ open, onClose, ids, cfg, onDone }) {
  const [d, setD] = useState(plusDays(7));
  const [t, setT] = useState("09:00");
  const [roomId, setRoomId] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setReason(""); } }, [open]);
  const run = async () => {
    if (!reason.trim()) { toast.error("A reset reason is required — it is copied to every case docket"); return; }
    setBusy(true);
    try {
      const r = await ujmsBatchReschedule({ hearing_ids: ids, new_date: d, new_time: t, reason: reason.trim(),
        courtroom_id: roomId || undefined });
      toast.success(`${r.data.moved} hearing(s) reset to ${d} ${t}`);
      const failed = r.data.results.filter((x) => !x.ok);
      if (failed.length) toast.warning(`${failed.length} skipped: ${[...new Set(failed.map((x) => x.reason))].join('; ')}`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Batch reset failed"); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="batch-reset-dialog">
        <DialogHeader><DialogTitle>Batch Reset — {ids.length} hearing(s)</DialogTitle></DialogHeader>
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-300 rounded-sm px-3 py-2 text-[12px] text-amber-800" data-testid="batch-reset-warning">
          <span className="text-amber-500 text-base leading-none">▲</span>
          Each selected setting is CONTINUED and re-set on the new session. The reason below is copied to every case docket
          (e.g., "Inclement weather"). Session-time warnings are informational — you may continue through them.
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="New date" required><input type="date" className={inputCls} value={d} onChange={(e) => setD(e.target.value)} data-testid="batch-reset-date" /></Field>
          <Field label="New time" required><input type="time" className={inputCls} value={t} onChange={(e) => setT(e.target.value)} data-testid="batch-reset-time" /></Field>
          <Field label="Move to courtroom (optional)">
            <select className={selectCls} value={roomId} onChange={(e) => setRoomId(e.target.value)} data-testid="batch-reset-room">
              <option value="">Keep each hearing's courtroom</option>
              {(cfg?.courtrooms || []).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </Field>
          <Field label="Reason (copied to all)" required>
            <input className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Inclement weather" data-testid="batch-reset-reason" />
          </Field>
        </div>
        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnPrimary} disabled={busy} onClick={run} data-testid="batch-reset-confirm">
            {busy ? "Rescheduling…" : "Reschedule All"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
